import { useTranslation } from 'react-i18next';


/**
 * The small form pieces the three commerce screens share.
 *
 * Bilingual name fields live here rather than being retyped on each screen because ASM-005 is a
 * rule about how clinic content is entered everywhere, not a decision each form makes for itself:
 * both languages optional, either one enough, and the hint that says so.
 */

export function Field({
  name,
  label,
  required,
  hint,
  dir,
  defaultValue,
}: {
  readonly name: string;
  readonly label: string;
  readonly required?: boolean;
  readonly hint?: string;
  readonly dir?: 'ltr';
  readonly defaultValue?: string;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        name={name}
        className="field__input"
        required={required}
        dir={dir}
        defaultValue={defaultValue}
      />
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

/** Arabic first, English second, neither required — the order Egyptian staff read in. */
export function BilingualNameFields({
  arDefault,
  enDefault,
}: {
  readonly arDefault?: string;
  readonly enDefault?: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Field name="nameAr" label={t('common.nameAr')} defaultValue={arDefault} hint={t('catalog.bilingualHint')} />
      <Field name="nameEn" label={t('common.nameEn')} dir="ltr" defaultValue={enDefault} />
    </>
  );
}

/**
 * A price, entered in pounds.
 *
 * Sent as a decimal string rather than a number, matching the API: a price parsed into a double
 * somewhere between here and the ledger is a price that eventually rounds.
 */
export function MoneyField({
  name,
  label,
  hint,
  defaultValue,
}: {
  readonly name: string;
  readonly label: string;
  readonly hint?: string;
  readonly defaultValue?: string;
}) {
  const { t } = useTranslation();
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        type="number"
        name={name}
        className="field__input numeric"
        min={0}
        step="0.01"
        inputMode="decimal"
        defaultValue={defaultValue}
        placeholder={t('catalog.pricePlaceholder')}
      />
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

